import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ADD_USER,
  GET_USER,
  EDIT_USER,
  ARCHIVE_USER,
  UNARCHIVE_USER,
  LOAD_USERS,
  LOAD_ARCHIVE_USERS,
  PROCESS_USERS,
  PROCESS_USER,
  PROCESS_NEW_USER,
  insertUser,
  removeUser,
  PROCESS_ARCHIVE_USER,
  PROCESS_UNARCHIVE_USER,
  USER_ERROR,
  USER_ADD_ERROR,
  USER_EDIT_ERROR,
  USER_ARCHIVE_ERROR,
  loadProcessedUsers,
  LOAD_USER_STATUSES,
  PROCESS_USER_STATUSES,
  insertUserStatuses,
  EDIT_USER_STATUS,
  USER_LOAD_ERROR,
  USER_STATUS_ERROR,
  EDIT_USER_PASSWORD,
  EDIT_USER_NOTES,
  EDIT_USER_IMAGE,
  getUser,
  insertCheckinModes,
  LOAD_CHECKIN_MODES,
  PROCESS_CHECKIN_MODES,
  CHECKIN_MODES_ERROR,
  PANIC_BUTTONS_ERROR,
  insertPanicButtons,
  PROCESS_PANIC_BUTTONS,
  LOAD_PANIC_BUTTONS,
  deleteAssociatedUserPanicButton,
  PROCESS_REMOVING_ASSOCIATED_USER_PANIC_BUTTON,
  REMOVE_ASSOCIATED_USER_PANIC_BUTTON,
  PROCESS_ASSOCIATED_USER_PANIC_BUTTON,
  ASSOCIATE_USER_PANIC_BUTTONS,
  LOAD_ASSOCIATED_USER_PANIC_BUTTONS,
  PROCESS_ASSOCIATED_USER_PANIC_BUTTONS,
  insertAssociatedUserPanicButton,
  PROCESS_EDIT_USER,
  IMPORT_USERS,
  EXPORT_USERS,
  CONFIRM_IMPORT_USERS,
  USERS_IMPORT_ERROR,
  PROCESS_EXPORT_USERS,
  loadUsers,
  REMOVE_USER_IMAGE,
  setLastUsersChecked,
  CREATE_MASSNOTIFICATION_DEVICE,
} from '../actions/userActions';
import { apiRequest } from '../actions/apiActions';
import {
  showLoading,
  hideLoading,
  addUiMsg,
  updateApiSpinner,
} from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  wasNotUpdatedText,
  newUserText,
  userText,
  wasNotArchivedText,
  wasArchivedText,
  wasUnArchivedText,
  wasUpdatedText,
  wasNotCreatedText,
  wasCreatedText,
} from '../../components/shared/SharedInternationalizations';
import { messageTypes } from '../../components/ui/AlertMessage';
import { addAlarmDeviceDetails } from '../actions/alarmDeviceActions';
import { addAlertDeviceDetails } from '../actions/alertDeviceActions';
import { getCurrentUserInfo } from '../actions/currentUserActions';
import { errorDisplay, redirect } from '../../utilities/general';
import { formPhpFullUrl } from '../../utilities/httputils';

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadAllUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_USERS) {
    const currentTime = new Date();
    const lastChecked = new Date(store.getState().users.lastChecked);
    let timeFilter = '';
    if (currentTime > lastChecked) {
      timeFilter = '&modified_date_gte=' + lastChecked.toISOString();
    }
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        '?is_active=True&is_interface=False' +
        timeFilter,
      onSuccess: PROCESS_USERS,
      onError: USER_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };

    store.dispatch(setLastUsersChecked(currentTime));
    store.dispatch(showLoading());
    store.dispatch(updateApiSpinner({ usersReturned: false }));
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === GET_USER) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload +
        '/',
      onSuccess: PROCESS_USER,
      onError: USER_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
      navigate: action.navigate,
      previous: action.previous,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the archive users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadArchiveUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ARCHIVE_USERS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        '?is_active=False&is_interface=False',
      onSuccess: PROCESS_USERS,
      onError: USER_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(updateApiSpinner({ usersReturned: true }));
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the returned users array from the api request.
 * @description - This takes the array, checks to see if the user already exists in the existing users object. If it does, it checks if the modified date of the returned user is newer than the existing user and if the returned user is newer, it will replace the current user, otherwise it will retain the current one. If the user doesn't exist already, it will create it and add it to the object
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#loadProcessedUnits - Dispatches the load processed users action to load the returned users into the Redux store
 */
export const processUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_USERS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_USERS,
        onError: USER_LOAD_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }
    const rawUsers = action.payload.results;

    store.dispatch(updateApiSpinner({ usersReturned: true }));

    let processedUsers = store.getState().users.users;
    rawUsers.map((user) => {
      if (!user.is_staff) {
        const incomingModifiedDate = new Date(user.modified_date);
        let previousModifiedDate;
        if (processedUsers[user.id]) {
          previousModifiedDate = new Date(
            processedUsers[user.id].modified_date
          );
        }
        if (
          !processedUsers[user.id] ||
          previousModifiedDate < incomingModifiedDate
        ) {
          processedUsers[user.id] = {
            ...user,
          };
        }

        return '';
      } else {
        return '';
      }
    });
    const currentTime = new Date();
    store.dispatch(loadProcessedUsers(processedUsers, currentTime));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const userLoadError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USER_LOAD_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.usersNotLoadedText"
          defaultMessage="Users not loaded"
          description="The users were not able to be loaded"
        />
      </React.Fragment>
    );

    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.usersNotLoadedErrorText"
          defaultMessage="The users were not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );

    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to add a new user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the user
 */
export const addUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === ADD_USER) {
    const userInfo = action.payload;

    // let formatteddate_of_birth = null;
    // if (userInfo.date_of_birth) {
    //   const date_of_birth = new Date(userInfo.date_of_birth);
    //   const date_of_birthYear = date_of_birth.getUTCFullYear();
    //   const date_of_birthMonth = date_of_birth.getUTCMonth() + 1;
    //   const date_of_birthDay = date_of_birth.getUTCDate();
    //   formatteddate_of_birth = `${date_of_birthYear}-${date_of_birthMonth}-${date_of_birthDay}`;
    // }

    const nextSteps = [];
    if (userInfo.addPhoto) {
      nextSteps.push('userImageEdit');
    }

    if (userInfo.isStaff) {
      nextSteps.push('createStaffOptions');
    }

    if (userInfo.usesCheckin) {
      nextSteps.push('createCheckInPreferences');
    }

    if (userInfo.makePhoneAlertDevice) {
      nextSteps.push('createAlertPhone');
    }

    if (userInfo.makeEmailAlertDevice) {
      nextSteps.push('createAlertEmail');
    }


    if (userInfo.makeMassnotification) {
      nextSteps.push('createMassnotification');
    }

    const prevSteps = ['userCreate'];

    const formSteps = {
      type: 'users',
      nextSteps,
      prevSteps,
    };

    let newUserName = '_gen_';
    let newUserPassword = '_gen_';

    if (userInfo.username && userInfo.isStaff) {
      newUserName = userInfo.username;
      newUserPassword = userInfo.password;
    }

    const body = {
      ...userInfo,
      username: newUserName,
      password: newUserPassword,
    'Mass notification work': userInfo['Mass notification work'],
    'Mass notification Text': userInfo['Mass notification Text'],
    'Mass notification Secondary Phone': userInfo['Mass notification Secondary Phone'],
    'work email': userInfo['work email'],
    'personal email': userInfo['personal email'],
    'secondary email': userInfo['secondary email'],
    };

    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].user,
      onSuccess: PROCESS_NEW_USER,
      onError: USER_ADD_ERROR,
      auth: store.getState().auth.token,
      body: true,
      preferences: action.payload,
      navigate: action.navigate,
      formSteps: formSteps,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to add the newly created user to the Redux store
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers adding the created user to the users object in Redux
 * @fires dispatch#reset - Resets the add new user form
 * @fires dispatch#userMsgAddSuccess - Triggers showing the user add success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processNewUser = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_NEW_USER) {
    dispatch(hideLoading());
    dispatch(insertUser(action.payload));
    const info = action;
    info.meta.formSteps.id = info.payload.id;
    if (
      info.meta.formSteps.nextSteps &&
      info.meta.formSteps.nextSteps.includes('createMassnotification')
    ) {

      
      info.meta.navigate(`/users/${info.payload.id}/edit/?mode=createMassnotification&next=&past=userCreate`, {
        state: {
          formData: info.meta.preferences, 
        },
      });
      return; 
    }
    let url = '/users/';
    redirect(info, url);

    const messageText = (
      <React.Fragment>
        {newUserText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.first_name} {action.payload.last_name}"
        </span>
        {wasCreatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgAddError - Triggers showing the user add error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const userAddError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === USER_ADD_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {newUserText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.first_name}
          {action.originalPayload.last_name}"
        </span>
        {wasNotCreatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const editUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER) {
    const user = store.getState().users.users[action.payload.id];
    const finalForm = { ...action.payload };
    if (!finalForm.title && user.title) {
      finalForm.title = '';
    }
    if (!finalForm.first_name && user.first_name) {
      finalForm.first_name = '';
    }
    if (!finalForm.middle_name && user.middle_name) {
      finalForm.middle_name = '';
    }
    if (!finalForm.last_name && user.last_name) {
      finalForm.last_name = '';
    }
    if (!finalForm.preferred_name && user.preferred_name) {
      finalForm.preferred_name = '';
    }
    if (!finalForm.suffix && user.suffix) {
      finalForm.suffix = '';
    }
    if (!finalForm.date_of_birth && user.date_of_birth) {
      finalForm.date_of_birth = null;
    }
    if (!finalForm.email && user.email) {
      finalForm.email = '';
    }
    if (!finalForm.phone && user.phone) {
      finalForm.phone = '';
    }
    if (!finalForm.notes && user.notes) {
      finalForm.notes = '';
    }

    let formatteddate_of_birth = null;
    if (finalForm.date_of_birth) {
      const date_of_birth = new Date(finalForm.date_of_birth);
      const date_of_birthYear = date_of_birth.getUTCFullYear();
      const date_of_birthMonth = date_of_birth.getUTCMonth() + 1;
      const date_of_birthDay = date_of_birth.getUTCDate();
      formatteddate_of_birth = `${date_of_birthYear}-${date_of_birthMonth}-${date_of_birthDay}`;
    }

    if (
      finalForm.active_unit &&
      !finalForm.units.includes(parseInt(finalForm.active_unit))
    ) {
      finalForm.active_unit = null;
    }

    const nextSteps = [];
    if (finalForm.addPhoto) {
      nextSteps.push('userImageEdit');
    }

    if (finalForm.isStaff && !user.preferences) {
      nextSteps.push('createStaffOptions');
    }

    if (finalForm.usesCheckin && !user.checkin_profile) {
      nextSteps.push('createCheckInPreferences');
    }

    if (finalForm.makePhoneAlertDevice) {
      nextSteps.push('createAlertPhone');
    }

    if (finalForm.makeEmailAlertDevice) {
      nextSteps.push('createAlertEmail');
    }

    if (finalForm.makeMassnotification) {
      nextSteps.push('createMassnotification');
    }

    const prevSteps = ['userEdit'];

    const formSteps = {
      type: 'users',
      nextSteps,
      prevSteps,
      id: action.payload.id,
    };

    const body = {
      ...finalForm,
      username: finalForm.username || user.username,
      date_of_birth: formatteddate_of_birth,
    };
    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      userObject: body,
      formSteps: formSteps,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processEditUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_EDIT_USER) {
    store.dispatch(hideLoading());
    store.dispatch(insertUser(action.payload));
    const info = action;
    if (info.meta.formSteps) {
      info.meta.formSteps.id = info.payload.id;
    }
    if (
      info.meta.formSteps &&
      info.meta.formSteps.nextSteps &&
      info.meta.formSteps.nextSteps.includes('createMassnotification')
    ) {

      
      info.meta.navigate(`/users/${info.payload.id}/edit/?mode=createMassnotification&next=&past=userEdit`, {
        state: {
          formData: info.meta.userObject, 
        },
      });
      return; 
    }
    if (action.meta.navigate) {
      const userId = action.payload?.id ?? action.meta.userObject?.id;
      let url = '/users/' + userId + '/view/';
      redirect(action, url);
    }
    const firstName = action.payload?.first_name ?? action.meta.userObject?.first_name ?? '';
    const lastName = action.payload?.last_name ?? action.meta.userObject?.last_name ?? '';
    const messageText = (
      <React.Fragment>
        {userText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{firstName} {lastName}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_USER) {
    store.dispatch(hideLoading());
    store.dispatch(insertUser(action.payload));
    const alarmArray = action.payload.alarm_devices;
    const alarmDetails = {};
    alarmArray.map((device) => {
      alarmDetails[device.alarm_device_id] = {
        alarm_device_id: device.alarm_device_id,
        alarm_description: device.alarm_description,
        unit: device.unit,
        user: device.user,
        alarm_device_type: device.alarm_device_type,
      };
      return '';
    });
    store.dispatch(addAlarmDeviceDetails(alarmDetails));
    const alertArray = action.payload.alert_devices;
    const alertDetails = {};
    alertArray.map((device) => {
      alertDetails[device.alert_device_id] = {
        alert_device_id: device.alert_device_id,
        alert_device_type: device.alert_device_type,
        alert_group_id: device.alert_group_id,
        unit: device.unit,
        user: device.user,
        email_address: device.email_address,
        phone_number: device.phone_number,
        interface_name: device.interface_name,
        alert_description: device.alert_description,
        notes: device.notes,
      };
      return '';
    });
    store.dispatch(addAlertDeviceDetails(alertDetails));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgEditError - Triggers showing the user edit error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const userEditError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === USER_EDIT_ERROR) {
    dispatch(hideLoading());
    let first_name = '';
    let last_name = '';
    if (action.meta.userObject) {
      first_name = action.meta.userObject.first_name;
    }
    if (action.meta.userObject) {
      last_name = action.meta.userObject.last_name;
    }

    const messageText = (
      <React.Fragment>
        {userText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{first_name} {last_name}"
        </span>
        {wasNotUpdatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const editUserStatus = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_STATUS) {
    const body = {
      status: action.payload.status,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      userObject: action.payload,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const editUserImage = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_IMAGE) {
    store.dispatch(showLoading());
    const user = action.payload.user;

    var formData = new FormData();
    if (action.payload.image) {
      formData.append('photo_file', action.payload.image);
    }
    //formData.append('caption', name);

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        user.id +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: formData,
      userObject: action.payload.user,
      navigate: action.meta && action.meta.navigate,
      previous: action.previous,
      componentOptions: {
        noCacheImage: action.meta?.noCacheImage ?? false,
      },
    };

    store.dispatch(apiRequest(formData, meta));
  }
};

/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const removeUserImage = (store) => (next) => (action) => {
  next(action);
  if (action.type === REMOVE_USER_IMAGE) {
    store.dispatch(showLoading());
    const user = action.payload.user;

    const meta = {
      method: 'DELETE',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].documentandimages +
        user.photo.docnode_pk +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: null,
      userObject: action.payload.user,
      navigate: action.meta && action.meta.navigate,
      previous: action.previous,
      componentOptions: {
        //noCacheImage: action.meta?.noCacheImage ?? false,
        noCacheImage: true,
      },
    } //meta

    store.dispatch(apiRequest(null, meta));

  } // REMOVE_USER_IMAGE
};

/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const editUserPassword = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_PASSWORD) {
    console.log('EDIT_USER_PASSWORD action payload:', action.payload);
    const body = {
      password: action.payload.password,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      userObject: action.payload,
      navigate: action.navigate,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to trigger an api call to edit a preexisting user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the user
 */
export const editUserNotes = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_USER_NOTES) {
    const body = {
      notes: action.payload.notes,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_EDIT_USER,
      onError: USER_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      userObject: action.payload,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to archive a user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to archive the user
 */
export const archiveUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === ARCHIVE_USER) {
    const body = {
      is_active: false,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_ARCHIVE_USER,
      onError: USER_ARCHIVE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware tounarchive a user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to archive the user
 */
export const unArchiveUser = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNARCHIVE_USER) {
    const body = {
      is_active: true,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].user +
        action.payload.id +
        '/',
      onSuccess: PROCESS_UNARCHIVE_USER,
      onError: USER_ARCHIVE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#userMsgArchiveError - Triggers showing the user archive error message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const userArchiveError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === USER_ARCHIVE_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {userText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.first_name}
          {action.originalPayload.last_name}"
        </span>
        {wasNotArchivedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the archived unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#removeUser - Triggers removing the unit from the redux store
 * @fires redirect - Navigates react-router to switch back to the units list view
 * @fires dispatch#userMsgArchiveSuccess - Triggers showing the user archive success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processArchiveUser = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ARCHIVE_USER) {
    dispatch(hideLoading());
    const url = '/users/';

    redirect(action, url);
    dispatch(removeUser(action.payload));
    const messageText = (
      <React.Fragment>
        {userText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.first_name} {action.payload.last_name}"
        </span>
        {wasArchivedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the unarchived unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#removeUser - Triggers removing the unit from the redux store
 * @fires redirect - Navigates react-router to switch back to the units list view
 * @fires dispatch#userMsgArchiveSuccess - Triggers showing the user unarchive success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processUnArchiveUser = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_UNARCHIVE_USER) {
    dispatch(hideLoading());
    const url = '/users/';

    redirect(action, url);
    dispatch(removeUser(action.payload));
    const messageText = (
      <React.Fragment>
        {userText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.first_name} {action.payload.last_name}"
        </span>
        {wasUnArchivedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const userError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === USER_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {newUserText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.first_name}
          {action.originalPayload.last_name}"
        </span>
        {wasNotUpdatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadUserStatuses = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_USER_STATUSES) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].userstatus,
      onSuccess: PROCESS_USER_STATUSES,
      onError: USER_STATUS_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processUserStatuses = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_USER_STATUSES) {
    store.dispatch(hideLoading());
    const data = action.payload;
    const statusArray = data.results;
    let statusObj = {};
    statusArray.map((status) => {
      statusObj[status.id] = status;
      return '';
    });

    store.dispatch(insertUserStatuses(statusObj));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const userStatusLoadError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USER_STATUS_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.userStatusNotLoadedText"
          defaultMessage="User Statuses not loaded"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );

    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.userStatusNotLoadedErrorText"
          defaultMessage="The user statuses were not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadCheckinModes = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_CHECKIN_MODES) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].mode,
      onSuccess: PROCESS_CHECKIN_MODES,
      onError: CHECKIN_MODES_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processCheckinModes = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_CHECKIN_MODES) {
    store.dispatch(hideLoading());
    const data = action.payload;
    const modesArray = data.results;
    let modesObj = {};
    modesArray.map((status) => {
      modesObj[status.mode_num] = status;
      return '';
    });
    store.dispatch(insertCheckinModes(modesObj));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const checkinModesLoadError = (store) => (next) => (action) => {
  next(action);
  if (action.type === CHECKIN_MODES_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.checkinModesNotLoadedText"
          defaultMessage="Check-in modes not loaded"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );

    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.checkinModesNotLoadedErrorText"
          defaultMessage="The check-in modes were not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadPanicButtons = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_PANIC_BUTTONS) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].panicbuttons,
      onSuccess: PROCESS_PANIC_BUTTONS,
      onError: PANIC_BUTTONS_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processPanicButtons = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_PANIC_BUTTONS) {
    store.dispatch(hideLoading());
    const data = action.payload;
    const array = data.results;
    let buttonsObj = {};
    array.map((button) => {
      buttonsObj[button.button_id] = button;
      return '';
    });
    store.dispatch(insertPanicButtons(buttonsObj));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const panicButtonsError = (store) => (next) => (action) => {
  next(action);
  if (action.type === PANIC_BUTTONS_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.panicButtonsNotLoadedText"
          defaultMessage="Panic Button list not loaded"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );

    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.panicButtonsNotLoadedErrorText"
          defaultMessage="The panic button list was not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const loadAssociatedUserPanicButtons = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ASSOCIATED_USER_PANIC_BUTTONS) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].userbuttons,
      onSuccess: PROCESS_ASSOCIATED_USER_PANIC_BUTTONS,
      onError: PANIC_BUTTONS_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processAssociatedUserPanicButtons = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ASSOCIATED_USER_PANIC_BUTTONS) {
    store.dispatch(hideLoading());
    const data = action.payload;
    const array = data.results;
    const users = store.getState().users.users;
    const userArr = Object.values(users);

    array.map((button) => {
      const filtered = userArr.filter((u) => u.username === button.username);
      const user = filtered[0];
      if (user) {
        const processedButton = {
          id: user.id,
          button: { [button.button_id]: { ...button } },
        };
        store.dispatch(insertAssociatedUserPanicButton(processedButton));
      } else {
        return '';
      }
      return '';
    });
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const associateUserPanicButtons = (store) => (next) => (action) => {
  next(action);
  if (action.type === ASSOCIATE_USER_PANIC_BUTTONS) {
    const body = action.payload;
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].userbuttons,
      onSuccess: PROCESS_ASSOCIATED_USER_PANIC_BUTTON,
      onError: PANIC_BUTTONS_ERROR,
      auth: store.getState().auth.token,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
      body: true,
      button: action.payload,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processAssociatedUserPanicButton = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ASSOCIATED_USER_PANIC_BUTTON) {
    store.dispatch(hideLoading());
    const data = action.payload;
    const users = store.getState().users.users;
    const userArr = Object.values(users).filter(
      (u) => u.username === data.username
    );
    const userObj = userArr[0];
    const payload = {
      id: userObj?.id,
      button: { [action.payload.button_id]: action.payload },
    };
    store.dispatch(insertAssociatedUserPanicButton(payload));

    if (action.meta.navigate) {
      let url = '/users/' + action.meta.button.userId + '/view/';
      redirect(action, url);
    }

    const messageText = (
      <React.Fragment>
        {userText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };
    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the users
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the users
 */
export const removeAssociatedUserPanicButton = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === REMOVE_ASSOCIATED_USER_PANIC_BUTTON) {
    const body = null;
    const meta = {
      method: 'DELETE',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].userbuttons +
        action.payload.button +
        '/',
      onSuccess: PROCESS_REMOVING_ASSOCIATED_USER_PANIC_BUTTON,
      onError: PANIC_BUTTONS_ERROR,
      auth: store.getState().auth.token,
      body: false,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
      button: action.payload,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processRemvoingAssociatedUserPanicButton = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_REMOVING_ASSOCIATED_USER_PANIC_BUTTON) {
    store.dispatch(deleteAssociatedUserPanicButton(action.meta.button));
    const users = store.getState().users.users;

    const userObj = users[action.meta.button.userId];
    store.dispatch(hideLoading());
    if (action.meta.navigate) {
      let url = '/users/' + action.meta.button.userId + '/view/';
      redirect(action, url);
    }
    const messageText = (
      <React.Fragment>
        {userText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{userObj?.first_name || ''} {userObj?.last_name || ''}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to add a new user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the user
 */
export const exportUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === EXPORT_USERS) {
    const body = '';
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].exportUsers,
      onSuccess: PROCESS_EXPORT_USERS,
      onError: USER_ERROR,
      auth: store.getState().auth.token,
      body: false,
      preferences: action.payload,
      navigate: action.navigate,
      previous: action.previous,
      payload: action.payload,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processExportUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_EXPORT_USERS) {
    store.dispatch(hideLoading());
    if (action.meta.navigate) {
      let url = '/users/';
      redirect(action, url);
    }
    action.meta.payload(action.payload);
  }
};
/**
 * Middleware to trigger an api call to add a new user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the user
 */
export const importUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === IMPORT_USERS) {
    const body = action.payload[0];
    const meta = {
      method: 'PUT',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].importUsers,
      onSuccess: CONFIRM_IMPORT_USERS,
      onError: USERS_IMPORT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      preferences: action.payload,
      navigate: action.navigate,
      previous: action.previous,
      csv: true,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires history.push - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const confirmImportUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === CONFIRM_IMPORT_USERS) {
    store.dispatch(hideLoading());
    if (action.meta.navigate) {
      let url = '/users/';
      if (action.meta.previous) {
        url = action.meta.previous;
      }

      redirect(action, url);
    }
    const messageText = (
      <FormattedMessage
        id="userMiddleware.usersImportConfirmText"
        defaultMessage="Users have been imported."
        description="Opens editing something"
      />
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    store.dispatch(loadUsers());
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const importUsersError = (store) => (next) => (action) => {
  next(action);
  if (action.type === USERS_IMPORT_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.importUsersError"
          defaultMessage="There was a problem importing your users"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );

    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
  }
};

export const createMassnotificationDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === CREATE_MASSNOTIFICATION_DEVICE) {
    const userInfo = action.payload;
    
    
     const massNotificationData = {
      user: userInfo.user || userInfo.id,
      alert_device_type: 54,
      phone_number: userInfo.phone || userInfo.primary_phone || userInfo['Phone'] || '',
      work_phone: userInfo['Mass notification work'] || userInfo.work_phone || '',
      cell_phone_number: userInfo['Mass notification Text'] || userInfo.text_phone || '',
      other_phone: userInfo['Mass notification Secondary Phone'] || userInfo.secondary_phone || '',
      // other_phone: userInfo.other_phone || '',
      work_email: userInfo['work email'] || userInfo.work_email || '',
      other_email: userInfo['personal email'] || userInfo.personal_email || '',
      email_address: userInfo['secondary email'] || userInfo.secondary_email || '',
      alert_description: userInfo.alert_description,
      notes: 'created mass notification device during user creation',
    };

    console.log('createMassnotificationDevice - sending to API:', massNotificationData);
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdevice,
      onSuccess: 'PROCESS_MASSNOTIFICATION_DEVICE',
      onError: 'MASSNOTIFICATION_DEVICE_ERROR',
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(massNotificationData, meta));
  }
};

/**
 * Middleware to process the created mass notification device
 */
export const processMassnotificationDevice = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === 'PROCESS_MASSNOTIFICATION_DEVICE') {
    // console.log('Mass notification device created successfully:', action.payload);
    dispatch(hideLoading());
    
    // Add the device to Redux store
    dispatch(addAlertDeviceDetails({ [action.payload.alert_device_id]: action.payload }));
    
    const userId = action.meta.formSteps.id;
    if (userId) {
      dispatch(getUser(userId));
    }

    // Redirect to the user view page
     try {
      if (action.meta && action.meta.navigate && action.meta.formSteps && action.meta.formSteps.id) {
        let url = '/users/' + action.meta.formSteps.id + '/view/';
        redirect(action, url);
      } else {
        console.warn('Missing meta information for redirect:', action.meta);
        // Fallback redirect if we have navigate but missing formSteps
        if (action.meta && action.meta.navigate) {
          action.meta.navigate('/users/');
        }
      }
    } catch (error) {
      console.error('Error during redirect:', error);
      // Try fallback redirect
      if (action.meta && action.meta.navigate) {
        action.meta.navigate('/users/');
      }
    }
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.massNotificationDeviceCreated"
          defaultMessage="Mass notification device created successfully"
          description="Mass notification device creation success message"
        />
      </React.Fragment>
    );
    
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to handle mass notification device creation errors
 */
export const massnotificationDeviceError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === 'MASSNOTIFICATION_DEVICE_ERROR') {
    // console.log('Mass notification device creation failed:', action.payload);
    dispatch(hideLoading());
    
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="userMiddleware.massNotificationDeviceError"
          defaultMessage="Failed to create mass notification device"
          description="Mass notification device creation error message"
        />
      </React.Fragment>
    );
    
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    
    dispatch(addUiMsg(message));
  }
};

export const userMdl = [
  loadAllUsers,
  loadArchiveUsers,
  processUsers,
  userError,
  processUser,
  addUser,
  processNewUser,
  loadUser,
  archiveUser,
  unArchiveUser,
  processArchiveUser,
  processUnArchiveUser,
  editUser,
  userAddError,
  userEditError,
  userArchiveError,
  processUserStatuses,
  loadUserStatuses,
  editUserStatus,
  editUserImage,
  userLoadError,
  userStatusLoadError,
  editUserPassword,
  editUserNotes,
  loadCheckinModes,
  processCheckinModes,
  checkinModesLoadError,
  loadPanicButtons,
  processPanicButtons,
  panicButtonsError,
  processAssociatedUserPanicButton,
  removeAssociatedUserPanicButton,
  processRemvoingAssociatedUserPanicButton,
  associateUserPanicButtons,
  loadAssociatedUserPanicButtons,
  processAssociatedUserPanicButtons,
  processEditUser,
  importUsers,
  confirmImportUsers,
  importUsersError,
  exportUsers,
  processExportUsers,
  removeUserImage,
  createMassnotificationDevice,
  processMassnotificationDevice,
  massnotificationDeviceError,
];
